import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { AlertCircle, ChevronLeft, Info, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  CoverImageDrop,
  type CoverImageResult,
} from "@/components/CoverImageDrop"
import {
  useCreatePurpose,
  useGetPurpose,
  useUpdatePurpose,
} from "@/lib/queries/purposes"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Purpose form (combined New + Edit)
// LEGACY: apps/admin/src/pages/AddPurpose.jsx (create) + inline modal in
//         apps/admin/src/pages/Purpose.jsx (update)
// API:    POST /admin/add_purpose     { purpose, S3Response }
//         POST /admin/update_purpose  { id, purpose }   (NAME ONLY)
//
// ─── Field parity (legacy ↔ v2) ──────────────────────────────────────
// CREATE payload:
//   purpose      ← Purpose name input
//   S3Response   ← FULL upload response object from /upload_video
//                  ({ s3response, file_name, s3_30sec_data })
//                  Capital 'S' preserved.
//
// UPDATE payload:
//   id           ← URL :id
//   purpose      ← Purpose name input
//   (NO image — backend doesn't support changing the image on update;
//   legacy update modal had no upload field either. v2 mirrors exactly:
//   the edit form only shows the name field with an info note that the
//   image can't be changed.)
//
// Validation parity:
//   - purpose name required (legacy: !purpose → toast)
//   - S3Response required on CREATE only (legacy: !S3UploadResponse → toast)
//
// IA NOTE: legacy used an inline modal for update; v2 promotes it to a
// dedicated /purposes/:id route to match the IA convention from earlier
// batches. Same effective behavior, cleaner navigation.
// ───────────────────────────────────────────────────────────────────────

const schema = z.object({
  purpose: z.string().min(1, "Purpose name is required"),
})

type FormValues = z.infer<typeof schema>

const DEFAULT_VALUES: FormValues = { purpose: "" }

export default function PurposeForm() {
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const navigate = useNavigate()

  const purposeQ = useGetPurpose(id)
  const createMut = useCreatePurpose()
  const updateMut = useUpdatePurpose()
  const submittingMut = isEdit ? updateMut : createMut

  const [coverResult, setCoverResult] = useState<CoverImageResult | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  })

  useEffect(() => {
    if (!isEdit || !purposeQ.data) return
    reset({ purpose: purposeQ.data.purpose ?? "" })
  }, [isEdit, purposeQ.data, reset])

  const existingImagePreview = isEdit ? purposeQ.data?.purposekey : undefined

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)

    try {
      if (isEdit && id) {
        const res = await updateMut.mutateAsync({
          id,
          purpose: values.purpose,
        })
        if (!res?.success) {
          setSubmitError(res?.message || "Failed to update purpose.")
          return
        }
      } else {
        if (!coverResult || !coverResult.s3response) {
          setSubmitError("Please upload a purpose image.")
          return
        }
        const res = await createMut.mutateAsync({
          purpose: values.purpose,
          S3Response: {
            s3response: coverResult.s3response,
            file_name: coverResult.file_name,
            s3_30sec_data: undefined, // image upload won't have this
          },
        })
        if (!res?.success) {
          setSubmitError(res?.message || "Failed to create purpose.")
          return
        }
      }
      navigate("/purposes")
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : `Failed to ${isEdit ? "update" : "create"} purpose.`
      )
    }
  })

  const isLoadingPrefill = isEdit && purposeQ.isLoading

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <a href="/purposes">
            <ChevronLeft className="mr-1 h-4 w-4" /> Back to purposes
          </a>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? "Edit purpose" : "Create purpose"}</CardTitle>
          <CardDescription>
            {isEdit
              ? purposeQ.data?.purpose || "Loading…"
              : "Both name and image are required."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEdit && purposeQ.isError && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load this purpose. Try refreshing.</span>
            </div>
          )}

          {isLoadingPrefill ? (
            <div className="grid h-32 place-items-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <form className="space-y-6" onSubmit={onSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="purpose">
                  Purpose name<span className="text-destructive"> *</span>
                </Label>
                <Input
                  id="purpose"
                  aria-invalid={!!errors.purpose}
                  {...register("purpose")}
                />
                {errors.purpose && (
                  <p className="text-xs text-destructive">
                    {errors.purpose.message}
                  </p>
                )}
              </div>

              {!isEdit ? (
                <div className="space-y-2">
                  <Label>
                    Image<span className="text-destructive"> *</span>
                  </Label>
                  <CoverImageDrop
                    result={coverResult}
                    onResult={setCoverResult}
                    onUploadingChange={setIsUploading}
                    disabled={submittingMut.isPending}
                  />
                </div>
              ) : (
                // Edit mode: image is read-only because the backend's
                // /admin/update_purpose endpoint only accepts {id, purpose}.
                // Show the existing image with an explanatory note instead
                // of a non-functional upload widget.
                <div className="space-y-2">
                  <Label>Image</Label>
                  <div className="flex items-start gap-3 rounded-md border bg-muted/20 p-3">
                    {existingImagePreview ? (
                      <img
                        src={existingImagePreview}
                        alt=""
                        className="h-20 w-20 rounded object-cover"
                      />
                    ) : (
                      <div className="grid h-20 w-20 place-items-center rounded bg-muted text-xs text-muted-foreground">
                        no image
                      </div>
                    )}
                    <div className="flex items-start gap-2 pt-1 text-xs text-muted-foreground">
                      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        The image can't be changed after a purpose is created.
                        Delete and recreate the purpose to use a different
                        image.
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {submitError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  disabled={isUploading || submittingMut.isPending}
                >
                  {(submittingMut.isPending || isUploading) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isUploading
                    ? "Uploading…"
                    : submittingMut.isPending
                      ? isEdit
                        ? "Saving…"
                        : "Creating…"
                      : isEdit
                        ? "Update purpose"
                        : "Create purpose"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/purposes")}
                  disabled={submittingMut.isPending}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
